import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Calculator, TrendingUp, Info, Plus, Trash2, AlertTriangle } from 'lucide-react';
import { PieChart, Pie, Cell, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const STORAGE_KEY = 'expense_calculator_data';

const DEFAULT_CATEGORIES = [
  { id: 1, name: 'Housing', amount: 15000, color: 'hsl(var(--chart-1))' },
  { id: 2, name: 'Food', amount: 8000, color: 'hsl(var(--chart-2))' },
  { id: 3, name: 'Transportation', amount: 5000, color: 'hsl(var(--chart-3))' },
  { id: 4, name: 'Entertainment', amount: 3000, color: 'hsl(var(--chart-4))' },
  { id: 5, name: 'Utilities', amount: 2500, color: 'hsl(var(--chart-5))' }
];

export const ExpenseCalculator = () => {
  const [expenses, setExpenses] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).expenses : DEFAULT_CATEGORIES;
  });

  const [timePeriod, setTimePeriod] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).timePeriod : 'monthly';
  });

  const [monthlyIncome, setMonthlyIncome] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).monthlyIncome : 50000;
  });

  const [newExpenseName, setNewExpenseName] = useState('');
  const [newExpenseAmount, setNewExpenseAmount] = useState('');

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      expenses,
      timePeriod,
      monthlyIncome
    }));
  }, [expenses, timePeriod, monthlyIncome]);

  const addExpense = () => {
    if (newExpenseName && newExpenseAmount) {
      const colors = [
        'hsl(var(--chart-1))',
        'hsl(var(--chart-2))',
        'hsl(var(--chart-3))',
        'hsl(var(--chart-4))',
        'hsl(var(--chart-5))'
      ];
      const newExpense = {
        id: Date.now(),
        name: newExpenseName,
        amount: parseFloat(newExpenseAmount) || 0,
        color: colors[expenses.length % colors.length]
      };
      setExpenses([...expenses, newExpense]);
      setNewExpenseName('');
      setNewExpenseAmount('');
    }
  };

  const deleteExpense = (id) => {
    setExpenses(expenses.filter(exp => exp.id !== id));
  };

  const updateExpenseAmount = (id, amount) => {
    setExpenses(expenses.map(exp => 
      exp.id === id ? { ...exp, amount: parseFloat(amount) || 0 } : exp
    ));
  };

  const calculateTotals = () => {
    const totalExpenses = expenses.reduce((sum, exp) => sum + exp.amount, 0);
    const income = parseFloat(monthlyIncome) || 0;
    const savings = income - totalExpenses;
    const savingsRate = income > 0 ? (savings / income) * 100 : 0;
    const averageDaily = totalExpenses / 30;

    return {
      totalExpenses,
      income,
      savings,
      savingsRate,
      averageDaily
    };
  };

  const generateTrendData = () => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
    const totals = calculateTotals();
    
    return months.map((month, index) => {
      const variance = (Math.random() - 0.5) * 0.2;
      const monthlyExpense = totals.totalExpenses * (1 + variance);
      
      return {
        month,
        expenses: Math.round(monthlyExpense),
        budget: totals.income,
        savings: Math.round(totals.income - monthlyExpense)
      };
    });
  };

  const generateBehaviorAnalysis = () => {
    const totals = calculateTotals();
    const sortedExpenses = [...expenses].sort((a, b) => b.amount - a.amount);
    
    return {
      topCategory: sortedExpenses[0]?.name || 'None',
      topAmount: sortedExpenses[0]?.amount || 0,
      lowestCategory: sortedExpenses[sortedExpenses.length - 1]?.name || 'None',
      lowestAmount: sortedExpenses[sortedExpenses.length - 1]?.amount || 0,
      categoryCount: expenses.length
    };
  };

  const generateCuttingOpportunities = () => {
    const sortedExpenses = [...expenses].sort((a, b) => b.amount - a.amount);
    const discretionaryCategories = ['Entertainment', 'Dining Out', 'Shopping', 'Subscriptions'];
    
    return sortedExpenses.slice(0, 3).map(exp => {
      const isDiscretionary = discretionaryCategories.some(cat => 
        exp.name.toLowerCase().includes(cat.toLowerCase())
      );
      const potentialSaving = exp.amount * (isDiscretionary ? 0.3 : 0.1);
      
      return {
        category: exp.name,
        current: exp.amount,
        potential: Math.round(potentialSaving),
        percentage: isDiscretionary ? 30 : 10
      };
    });
  };

  const totals = calculateTotals();
  const trendData = generateTrendData();
  const behaviorAnalysis = generateBehaviorAnalysis();
  const cuttingOpportunities = generateCuttingOpportunities();

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const COLORS = [
    'hsl(var(--chart-1))',
    'hsl(var(--chart-2))',
    'hsl(var(--chart-3))',
    'hsl(var(--chart-4))',
    'hsl(var(--chart-5))'
  ];

  return (
    <div className="min-h-screen bg-gradient-hero">
      <nav className="border-b border-border/40 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              </Link>
              <Separator orientation="vertical" className="h-6" />
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg gradient-primary flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-white" />
                </div>
                <span className="font-display text-lg font-semibold text-foreground">
                  Expense Calculator
                </span>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="calculator" className="space-y-6">
          <div className="overflow-x-auto scrollbar-hide -mx-4 px-4">
            <TabsList className="inline-flex w-auto min-w-full sm:min-w-0 bg-muted/50 p-1 h-auto">
              <TabsTrigger 
                value="calculator" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <Calculator className="w-4 h-4 mr-2" />
                Calculator & Results
              </TabsTrigger>
              <TabsTrigger 
                value="charts" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Visual Analysis
              </TabsTrigger>
              <TabsTrigger 
                value="insights" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <Info className="w-4 h-4 mr-2" />
                Insights & Tips
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="calculator" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Track Your Expenses</CardTitle>
                <CardDescription className="text-base leading-relaxed">
                  Monitor your spending habits, identify cost-cutting opportunities, and achieve better financial control 
                  by tracking all your expenses in one place.
                </CardDescription>
              </CardHeader>
            </Card>

            <div className="grid lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-1 border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Add Expenses</CardTitle>
                  <CardDescription>Track your spending by category</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="monthlyIncome" className="text-sm font-medium">
                      Monthly Income ($)
                    </Label>
                    <Input
                      id="monthlyIncome"
                      type="number"
                      value={monthlyIncome}
                      onChange={(e) => setMonthlyIncome(e.target.value)}
                      placeholder="50000"
                      className="text-base"
                    />
                  </div>

                  <Separator />

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="expenseName" className="text-sm font-medium">
                        Expense Category
                      </Label>
                      <Input
                        id="expenseName"
                        type="text"
                        value={newExpenseName}
                        onChange={(e) => setNewExpenseName(e.target.value)}
                        placeholder="e.g., Groceries"
                        className="text-base"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="expenseAmount" className="text-sm font-medium">
                        Amount ($)
                      </Label>
                      <Input
                        id="expenseAmount"
                        type="number"
                        value={newExpenseAmount}
                        onChange={(e) => setNewExpenseAmount(e.target.value)}
                        placeholder="5000"
                        className="text-base"
                      />
                    </div>

                    <Button 
                      className="w-full gradient-secondary hover:opacity-90 transition-opacity"
                      onClick={addExpense}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Expense
                    </Button>
                  </div>

                  <Separator />

                  <div className="space-y-3 max-h-[300px] overflow-y-auto">
                    <h3 className="text-sm font-medium text-muted-foreground">Current Expenses</h3>
                    {expenses.map((expense) => (
                      <div key={expense.id} className="flex items-center gap-2 p-3 rounded-lg bg-muted/30">
                        <div className="flex-1">
                          <p className="font-medium text-sm">{expense.name}</p>
                          <Input
                            type="number"
                            value={expense.amount}
                            onChange={(e) => updateExpenseAmount(expense.id, e.target.value)}
                            className="mt-1 h-8"
                          />
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteExpense(expense.id)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <div className="lg:col-span-2 space-y-6">
                <Card className="border-border/50 shadow-md">
                  <CardHeader>
                    <CardTitle className="font-display">Expense Summary</CardTitle>
                    <CardDescription>Your spending overview</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="p-6 rounded-xl bg-gradient-to-br from-primary-light to-card border border-primary/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                            <TrendingUp className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Total Expenses</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-primary">
                          {formatCurrency(totals.totalExpenses)}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">per month</p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-secondary-light to-card border border-secondary/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center">
                            <Calculator className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Monthly Income</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-secondary">
                          {formatCurrency(totals.income)}
                        </p>
                      </div>

                      <div className={`p-6 rounded-xl bg-gradient-to-br ${totals.savings >= 0 ? 'from-accent-light to-card border-accent/20' : 'from-destructive/10 to-card border-destructive/20'} border`}>
                        <div className="flex items-center gap-3 mb-2">
                          <div className={`w-10 h-10 rounded-lg ${totals.savings >= 0 ? 'bg-accent' : 'bg-destructive'} flex items-center justify-center`}>
                            {totals.savings >= 0 ? <TrendingUp className="w-5 h-5 text-accent-foreground" /> : <AlertTriangle className="w-5 h-5 text-white" />}
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Savings</p>
                        </div>
                        <p className={`text-3xl font-display font-bold ${totals.savings >= 0 ? 'text-accent-foreground' : 'text-destructive'}`}>
                          {formatCurrency(totals.savings)}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          {totals.savingsRate.toFixed(1)}% of income
                        </p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-purple-light to-card border border-purple/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-purple flex items-center justify-center">
                            <Info className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Average Daily</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-purple">
                          {formatCurrency(totals.averageDaily)}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">spending per day</p>
                      </div>
                    </div>

                    <Separator className="my-6" />

                    <div className="p-6 rounded-xl bg-gradient-chart">
                      <h3 className="text-lg font-display font-semibold mb-4">Financial Health</h3>
                      <div className="space-y-4">
                        <div>
                          <div className="flex justify-between text-sm mb-2">
                            <span>Expense Ratio</span>
                            <span className="font-semibold">
                              {((totals.totalExpenses / totals.income) * 100).toFixed(1)}%
                            </span>
                          </div>
                          <div className="h-2 bg-muted rounded-full overflow-hidden">
                            <div 
                              className={`h-full ${
                                totals.totalExpenses / totals.income > 0.8 ? 'bg-destructive' :
                                totals.totalExpenses / totals.income > 0.5 ? 'bg-accent' :
                                'bg-secondary'
                              }`}
                              style={{ width: `${Math.min((totals.totalExpenses / totals.income) * 100, 100)}%` }}
                            />
                          </div>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {totals.totalExpenses / totals.income > 0.8 ? 
                            '⚠️ High expense ratio. Consider reducing spending.' :
                           totals.totalExpenses / totals.income > 0.5 ?
                            '⚡ Moderate spending. Room for improvement.' :
                            '✅ Excellent! You\'re maintaining healthy finances.'}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="charts" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Category-wise Expense Distribution</CardTitle>
                <CardDescription className="text-base">
                  See where your money goes
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={expenses}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                        outerRadius={120}
                        fill="#8884d8"
                        dataKey="amount"
                      >
                        {expenses.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Expense Trend</CardTitle>
                  <CardDescription>
                    Monthly spending pattern
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={trendData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis 
                          dataKey="month" 
                          stroke="hsl(var(--muted-foreground))"
                        />
                        <YAxis 
                          stroke="hsl(var(--muted-foreground))"
                          tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => formatCurrency(value)}
                        />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="expenses" 
                          stroke="hsl(var(--primary))" 
                          strokeWidth={2}
                          name="Expenses"
                        />
                        <Line 
                          type="monotone" 
                          dataKey="budget" 
                          stroke="hsl(var(--secondary))" 
                          strokeWidth={2}
                          strokeDasharray="5 5"
                          name="Income"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">High-Spend Alert</CardTitle>
                  <CardDescription>
                    Categories consuming most of your budget
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={[...expenses].sort((a, b) => b.amount - a.amount).slice(0, 5)}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis 
                          dataKey="name" 
                          stroke="hsl(var(--muted-foreground))"
                          angle={-15}
                          textAnchor="end"
                          height={80}
                        />
                        <YAxis 
                          stroke="hsl(var(--muted-foreground))"
                          tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => formatCurrency(value)}
                        />
                        <Bar dataKey="amount" name="Amount">
                          {expenses.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="insights" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Spending Insights & Recommendations</CardTitle>
                <CardDescription className="text-base">
                  Personalized analysis of your spending habits
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-primary">
                    Spending Behavior Analysis
                  </h3>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div className="p-4 rounded-lg bg-primary-light border border-primary/20">
                      <p className="text-sm text-muted-foreground mb-1">Top Category</p>
                      <p className="text-lg font-bold text-primary mb-1">{behaviorAnalysis.topCategory}</p>
                      <p className="text-sm text-muted-foreground">{formatCurrency(behaviorAnalysis.topAmount)}</p>
                    </div>
                    <div className="p-4 rounded-lg bg-secondary-light border border-secondary/20">
                      <p className="text-sm text-muted-foreground mb-1">Lowest Category</p>
                      <p className="text-lg font-bold text-secondary mb-1">{behaviorAnalysis.lowestCategory}</p>
                      <p className="text-sm text-muted-foreground">{formatCurrency(behaviorAnalysis.lowestAmount)}</p>
                    </div>
                    <div className="p-4 rounded-lg bg-accent-light border border-accent/20">
                      <p className="text-sm text-muted-foreground mb-1">Categories Tracked</p>
                      <p className="text-lg font-bold text-accent-foreground mb-1">{behaviorAnalysis.categoryCount}</p>
                      <p className="text-sm text-muted-foreground">expense types</p>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-secondary">
                    Cost-Cutting Opportunities
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-4">
                    Based on your spending pattern, here are potential areas where you can save money:
                  </p>
                  <div className="space-y-3">
                    {cuttingOpportunities.map((opp, index) => (
                      <div key={index} className="p-4 rounded-lg bg-secondary-light border border-secondary/20">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-semibold text-foreground">{opp.category}</p>
                            <p className="text-sm text-muted-foreground">Current: {formatCurrency(opp.current)}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-secondary">Save {formatCurrency(opp.potential)}</p>
                            <p className="text-xs text-muted-foreground">({opp.percentage}% reduction)</p>
                          </div>
                        </div>
                        <p className="text-sm text-muted-foreground mt-2">
                          💡 Try reducing this expense by {opp.percentage}% to save {formatCurrency(opp.potential * 12)} annually
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-accent-foreground">
                    Habit-Based Suggestions
                  </h3>
                  <div className="space-y-4 text-base">
                    <div className="p-4 rounded-lg bg-primary-light border-l-4 border-primary">
                      <h4 className="font-semibold text-foreground mb-2">✅ Follow the 50/30/20 Rule</h4>
                      <p className="text-muted-foreground">
                        Allocate 50% to needs, 30% to wants, and 20% to savings. Currently: Needs {((totals.totalExpenses * 0.6 / totals.income) * 100).toFixed(0)}%, 
                        Wants {((totals.totalExpenses * 0.4 / totals.income) * 100).toFixed(0)}%, Savings {totals.savingsRate.toFixed(0)}%
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-secondary-light border-l-4 border-secondary">
                      <h4 className="font-semibold text-foreground mb-2">✅ Track Daily Spending</h4>
                      <p className="text-muted-foreground">
                        You're spending an average of {formatCurrency(totals.averageDaily)} per day. Set a daily limit to avoid overspending.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-accent-light border-l-4 border-accent">
                      <h4 className="font-semibold text-foreground mb-2">✅ Automate Savings</h4>
                      <p className="text-muted-foreground">
                        Set up automatic transfers of at least {formatCurrency(totals.income * 0.2)} (20% of income) to a savings account.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-purple-light border-l-4 border-purple">
                      <h4 className="font-semibold text-foreground mb-2">✅ Review Subscriptions</h4>
                      <p className="text-muted-foreground">
                        Cancel unused subscriptions and memberships. Even $500/month saved = $6,000/year!
                      </p>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-purple">
                    Monthly Budget Alignment
                  </h3>
                  <div className="p-6 rounded-xl bg-gradient-to-br from-purple-light to-card border border-purple/20">
                    <div className="space-y-4">
                      <div className="flex justify-between items-center pb-3 border-b border-border/50">
                        <span className="text-sm font-medium">Monthly Income</span>
                        <span className="text-lg font-bold text-foreground">{formatCurrency(totals.income)}</span>
                      </div>
                      <div className="flex justify-between items-center pb-3 border-b border-border/50">
                        <span className="text-sm font-medium">Total Expenses</span>
                        <span className="text-lg font-bold text-primary">{formatCurrency(totals.totalExpenses)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">Available Balance</span>
                        <span className={`text-lg font-bold ${totals.savings >= 0 ? 'text-secondary' : 'text-destructive'}`}>
                          {formatCurrency(totals.savings)}
                        </span>
                      </div>
                      <div className="pt-3 border-t border-border/50">
                        <p className="text-sm text-muted-foreground">
                          {totals.savings >= 0 ? 
                            `Great job! You're saving ${totals.savingsRate.toFixed(1)}% of your income. Keep it up!` :
                            `You're overspending by ${formatCurrency(Math.abs(totals.savings))}. Review your expenses and cut unnecessary costs.`
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ExpenseCalculator;
